<template>
  <section class="p1 subtle">
    <h2 class="mb1 left">Organization details</h2>
    <p class="align-right m0">
      <button
        class="invisible-button small-button mt1"
        @click.prevent="isEditing =! isEditing">
        <template v-if="!isEditing">Edit</template>
        <template v-else>Done</template>
      </button>
    </p>
    <hr class="mb1 c1 black-border border-width-3 clear">
    <div v-if="!isEditing">
      <h4
        v-if="!isEditing"
        class="clear sans medium grey-text-500">Organization name <span class="block accent-text bold font-size-5 display mb0">{{ organization.name }}</span></h4>
      <div v-if="!isEditing && organization.logo">
        <h4 class="mb1 medium">Organization logo</h4>
        <lw-image
          v-if="organization.logo"
          :src="organization.logo"
          :alt="organization.name + ' logo'"
          :class="'org-logo'"/>
      </div>
      <h4
        v-if="!isEditing"
        class="clear sans medium grey-text-500">Currency symbol <span class="block accent-text bold font-size-5 display mb0">{{ organization.currency }}</span></h4>
      <div
        v-if="organization.theme"
        :style="{ backgroundColor: organization.theme.background, color: organization.theme.foreground, }"
        class="p1 mt1">
        <h3 class="mt0 mb1 font-size-4">Theme</h3>
        <p class="clear">Background colour<span class="block bold">{{ organization.theme.background }}</span></p>
        <p class="clear" >Text colour<span class="block bold">{{ organization.theme.foreground }}</span></p>
        <p
          :style="{ color: organization.theme.accent }"
          class="clear">Accent colour<span class="block bold">{{ organization.theme.accent }}</span></p>
        <p
          v-if="organization.theme.accent2"
          :style="{ color: organization.theme.accent2 }"
          class="clear">Secondary accent colour<span class="block bold">{{ organization.theme.accent2 }}</span></p>
        <p
          v-if="organization.theme.headline"
          class="clear mbo">Headline font<span class="block bold display">{{ organization.theme.headline }}</span></p>
        <p
          v-if="organization.theme.headlineWeight"
          class="clear mbo">Headline font weight<span class="block bold display">{{ headlineWeight }}</span></p>
      </div>
    </div>
    <form
      v-else
      class="mb1 pt1 clear"
      @submit.prevent="updateOrg">
      <lw-text-input
        v-validate="'required'"
        :id="'organization'"
        :label="'Organization name'"
        :name="'organization'"
        v-model="name"
        :error="errors.first('organization')"/>
      <div v-if="organization.logo && logo === organization.logo">
        <lw-image
          v-if="organization.logo"
          :src="organization.logo"
          :alt="organization.name + ' logo'"
          :class="'org-logo'"/>
        <button
          class="small-button dark-red-button"
          @click.prevent="removeLogo">Remove logo</button>
      </div>
      <lw-upload
        :name="'logo'"
        :placeholder="logo"
        :id="'logo'"
        v-model="logo"
        :hint="'Max file size: 5MB.<br>Acceptable file formats are PNG, JPG, SVG, and GIF.<br>Logo may take a few minutes to display.'"
        :error="errors.first('logo')">Upload logo</lw-upload>
      <lw-text-input
        v-validate="'required'"
        :id="'currency'"
        :label="'Currency symbol'"
        :name="'currency'"
        :maxlength="1"
        v-model="currency"
        :error="errors.first('currency')"/>
      <lw-text-input
        :id="'background'"
        :label="'Background colour'"
        :name="'background'"
        v-model="background"/>
      <lw-text-input
        :id="'foreground'"
        :label="'Text colour'"
        :name="'foreground'"
        v-model="foreground"/>
      <lw-text-input
        :id="'accent'"
        :label="'Accent colour'"
        :name="'accent'"
        v-model="accent"/>
      <lw-text-input
        :id="'accent2'"
        :label="'Secondary accent colour'"
        :name="'accent2'"
        v-model="accent2"/>
      <lw-text-input
        :id="'headline'"
        :label="'Override headline font'"
        :name="'headline'"
        :hint="'Local font must be installed on your system. Results may not be perfect.'"
        v-model="headline"/>
      <lw-select-input
        v-if="headline"
        :id="'headlineWeight'"
        :label="'Headline weight'"
        :name="'headlineWeight'"
        v-model="headlineWeight"
        :options="headlineWeights"/>
      <button
        v-shortkey="['meta', 's']"
        type="submit"
        class="button accent-button full-button_mb"
        @shortkey="updateOrg()">Update</button>
    </form>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { slugify } from '../mixins/slugify'
import TextInput from '../livewires/TextInput'
import SelectInput from '../livewires/SelectInput'
import Upload from '../livewires/Upload'
import Image from '../livewires/Image'

export default {
  components: {
    lwTextInput: TextInput,
    lwSelectInput: SelectInput,
    lwUpload: Upload,
    lwImage: Image
  },
  mixins: [slugify],
  data () {
    return {
      name: '',
      logo: '',
      background: '',
      foreground: '',
      accent: '',
      accent2: '',
      headline: '',
      headlineWeight: '',
      headlineWeights: [
        {
          value: 'thin',
          label: 'Thin'
        },
        {
          value: 'light',
          label: 'Light'
        },
        {
          value: 'book',
          label: 'Book'
        },
        {
          value: 'regular',
          label: 'Regular'
        },
        {
          value: 'medium',
          label: 'Medium'
        },
        {
          value: 'semibold',
          label: 'Semibold'
        },
        {
          value: 'bold',
          label: 'Bold'
        },
        {
          value: 'extrabold',
          label: 'Extrabold'
        },
        {
          value: 'heavy',
          label: 'Heavy'
        }
      ],
      currency: '',
      isEditing: false
    }
  },
  computed: {
    ...mapGetters([
      'organization',
      'user',
      'users'
    ])
  },
  mounted () {
    this.name = this.organization.name
    this.currency = this.organization.currency
    this.logo = this.organization.logo
    if (this.organization.theme) {
      this.background = this.organization.theme.background
      this.foreground = this.organization.theme.foreground
      this.accent = this.organization.theme.accent
      this.accent2 = this.organization.theme.accent2
      if (this.organization.theme.headline) {
        this.headline = this.organization.theme.headline
      } else {
        this.headlineWeight = 'bold'
      }
      if (this.organization.theme.headlineWeight) {
        this.headlineWeight = this.organization.theme.headlineWeight
      } else {
        this.headlineWeight = 'bold'
      }
    } else {
      this.background = '#DCDBD7'
      this.foreground = '#322E2F'
      this.accent = '#FF592D'
      this.accent2 = '#0031CE'
    }
  },
  methods: {
    removeLogo () {
      this.logo = null
    },
    updateOrg () {
      this.$store.dispatch('saveState', false)
      if (!this.background) {
        this.background = '#DCDBD7'
      }
      if (!this.foreground) {
        this.foreground = '#322E2F'
      }
      if (!this.accent) {
        this.accent = '#FF592D'
      }
      if (!this.accent2) {
        this.accent2 = '#0031CE'
      }
      if (!this.headline) {
        this.headlineWeight = ''
      }
      const org = {
        id: this.organization.id,
        name: this.name,
        members: this.organization.members,
        currency: this.currency,
        theme: {
          background: this.background,
          foreground: this.foreground,
          accent: this.accent,
          accent2: this.accent2,
          headline: this.headline,
          headlineWeight: this.headlineWeight
        }
      }
      if (this.logo !== this.organization.logo) {
        org.logo = this.logo
      }
      this.isEditing = false
      this.$store.dispatch('updateOrganization', org).then(response => {
        this.logo = this.organization.logo
      })
    }
  }
}
</script>

<style>
.org-logo img,
.org-logo svg {
  height: calc(var(--line) * 1.5);
}
</style>
